const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function getToken() {
  return localStorage.getItem('admin_token');
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function req(method, path, body, { auth = true } = {}) {
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? authHeaders() : {}),
    },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const r = await fetch(`${API_BASE}${path}`, opts);
  // En el login un 401 es "credenciales inválidas", no sesión vencida:
  // no redirigir ni borrar storage (si no parece un bucle al panel).
  if (r.status === 401 && auth) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_nombre');
    window.location.replace('/admin/login');
    throw new Error('Sesión expirada');
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `Error ${r.status}`);
  return data;
}

// ── Auth ───────────────────────────────────────────────────────────────────────
export async function adminLogin(login, password) {
  // Sin Authorization: evita mandar un token viejo/inválido al pedir uno nuevo.
  const data = await req('POST', '/api/admin/login', { login, password }, { auth: false });
  if (!data?.token) throw new Error('No se recibió token de sesión');
  localStorage.setItem('admin_token', data.token);
  localStorage.setItem('admin_id', data.id ?? '');
  localStorage.setItem('admin_nombre', data.nombre ?? '');
  localStorage.setItem('admin_test_mode', data.test_mode ? '1' : '0');
  localStorage.setItem('admin_is_admin', data.admin ? '1' : '0');
  return data;
}

export function adminLogout() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_id');
  localStorage.removeItem('admin_nombre');
  localStorage.removeItem('admin_test_mode');
  localStorage.removeItem('admin_is_admin');
}

export function isAdminLoggedIn() {
  return !!getToken();
}

export function getAdminId() {
  return Number(localStorage.getItem('admin_id')) || null;
}

export function getAdminNombre() {
  return localStorage.getItem('admin_nombre') ?? '';
}

export function isTestModeEnabled() {
  return localStorage.getItem('admin_test_mode') === '1';
}

export function isAdmin() {
  return localStorage.getItem('admin_is_admin') === '1';
}

// ── Upload ────────────────────────────────────────────────────────────────────
export async function uploadAdminFile(file, folder = 'images', filename) {
  const form = new FormData();
  form.append('file', file);
  form.append('folder', folder);
  if (filename) form.append('filename', filename);
  const r = await fetch(`${API_BASE}/api/admin/storage/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (r.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_nombre');
    window.location.replace('/admin/login');
    return;
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `Error ${r.status}`);
  return data;
}

// ── Helpers genéricos ─────────────────────────────────────────────────────────
export const adminGet    = (path)         => req('GET',    path);
export const adminPost   = (path, body)   => req('POST',   path, body);
export const adminPut    = (path, body)   => req('PUT',    path, body);
export const adminDelete = (path)         => req('DELETE', path);

// ── Lenguajes ─────────────────────────────────────────────────────────────────
export const getLenguajes       = ()        => adminGet('/api/admin/lenguajes');
export const createLenguaje     = (d)       => adminPost('/api/admin/lenguajes', d);
export const updateLenguaje     = (id, d)   => adminPut(`/api/admin/lenguajes/${id}`, d);
export const deleteLenguaje     = (id)      => adminDelete(`/api/admin/lenguajes/${id}`);

// ── Categorías (TipoTest) ─────────────────────────────────────────────────────
export const getCategorias      = ()        => adminGet('/api/admin/categorias');
export const getCategoria       = (id)      => adminGet(`/api/admin/categorias/${id}`);
export const createCategoria    = (d)       => adminPost('/api/admin/categorias', d);
export const updateCategoria    = (id, d)   => adminPut(`/api/admin/categorias/${id}`, d);
export const deleteCategoria    = (id)      => adminDelete(`/api/admin/categorias/${id}`);

// ── Tareas (TipoSubTest) ──────────────────────────────────────────────────────
export const getTareas          = (catId)   => adminGet(`/api/admin/tareas${catId ? `?categoriaId=${catId}` : ''}`);
export const getTarea           = (id)      => adminGet(`/api/admin/tareas/${id}`);
export const createTarea        = (d)       => adminPost('/api/admin/tareas', d);
export const updateTarea        = (id, d)   => adminPut(`/api/admin/tareas/${id}`, d);
export const deleteTarea        = (id)      => adminDelete(`/api/admin/tareas/${id}`);

// ── Tests (genérico por tipo) ─────────────────────────────────────────────────
export const TEST_TIPOS = [
  { key: 'libretest',                    label: 'Libre Test' },
  { key: 'rotacion',                     label: 'Rotación' },
  { key: 'priming',                      label: 'Priming' },
  { key: 'memoriaacortopalabras',        label: 'Memoria a Corto Plazo' },
  { key: 'digitosdirectosinversos',      label: 'Dígitos Directos/Inversos' },
  { key: 'aprendizajesuperficialprofundo', label: 'Aprendizaje Sup./Prof.' },
];

export const getTests    = (tipo, tareaId)  => adminGet(`/api/admin/tests/${tipo}${tareaId ? `?tareaId=${tareaId}` : ''}`);
export const getTest     = (tipo, id)       => adminGet(`/api/admin/tests/${tipo}/${id}`);
export const createTest  = (tipo, d)        => adminPost(`/api/admin/tests/${tipo}`, d);
export const updateTest  = (tipo, id, d)    => adminPut(`/api/admin/tests/${tipo}/${id}`, d);
export const deleteTest  = (tipo, id)       => adminDelete(`/api/admin/tests/${tipo}/${id}`);

// ── ListasValores ─────────────────────────────────────────────────────────────
export const getListas      = ()        => adminGet('/api/admin/listas');
export const getLista       = (id)      => adminGet(`/api/admin/listas/${id}`);
export const createLista    = (d)       => adminPost('/api/admin/listas', d);
export const updateLista    = (id, d)   => adminPut(`/api/admin/listas/${id}`, d);
export const deleteLista    = (id)      => adminDelete(`/api/admin/listas/${id}`);

// ── Secuencias ────────────────────────────────────────────────────────────────
export const getSecuencias    = (listaId)   => adminGet(`/api/admin/listas/${listaId}/secuencias`);
export const createSecuencia  = (listaId,d) => adminPost(`/api/admin/listas/${listaId}/secuencias`, d);
export const updateSecuencia  = (id, d)     => adminPut(`/api/admin/secuencias/${id}`, d);
export const deleteSecuencia  = (id)        => adminDelete(`/api/admin/secuencias/${id}`);
export const vincularImagenSecuencia = (id, campo, url, force = false) =>
  adminPost('/api/admin/secuencias/vincular-imagen', { id, campo, url, force });

// ── TestMode ──────────────────────────────────────────────────────────────────
export const setTestMode   = (entity, id) => adminPost('/api/admin/testmode', { entity, entity_id: id });
export const unsetTestMode = (entity, id) => adminDelete(`/api/admin/testmode/${entity}/${id}`);

// ── PWA (instalaciones) ─────────────────────────────────────────────────────
export const getPwaStats = () => adminGet('/api/admin/pwa-stats');

// ── PWA (notificaciones push) ────────────────────────────────────────────────
export const getPushSubscribersCount = () => adminGet('/api/admin/push/subscribers-count');
export const getPushSubscribers      = () => adminGet('/api/admin/push/subscribers');
export const getPushNotifications    = () => adminGet('/api/admin/push/notifications');
export const createPushNotification  = (d) => adminPost('/api/admin/push/notifications', d);

// ── Usuarios ──────────────────────────────────────────────────────────────────
export const getUsuarios    = ()        => adminGet('/api/admin/usuarios');
export const createUsuario  = (d)       => adminPost('/api/admin/usuarios', d);
export const updateUsuario  = (id, d)   => adminPut(`/api/admin/usuarios/${id}`, d);
export const deleteUsuario  = (id)      => adminDelete(`/api/admin/usuarios/${id}`);

// ── Configuraciones ───────────────────────────────────────────────────────────
export const getDashboardUmbrales   = ()  => adminGet('/api/admin/config/dashboard-umbrales');
export const saveDashboardUmbrales  = (d) => adminPut('/api/admin/config/dashboard-umbrales', { umbrales: d });
export const resetDashboardUmbrales = ()  => adminPost('/api/admin/config/dashboard-umbrales/reset');
